/**
 * LotAllocationMassUploadDialog — bulk insert nhiều dòng Lot Allocation cùng
 * lúc. Pattern tham khảo nút "Mass upload" ở `/futures/orders`
 * (FuturesOrdersListView): grid editable + paste TSV từ Excel + 1 POST bulk.
 *
 * Cột (theo field bảng `lot_allocation`): Lot ID, Stack ID, GRN ID (optional),
 * Bag Allocated, Kg Allocated. Validate client: required lot_id/stack_id +
 * bag/kg là số > 0 (mirror Access `strCheck`). BE re-validate + insert trong 1
 * transaction (lỗi 1 dòng → rollback hết). KHÔNG enforce Pending balance ở
 * bulk path — single-form dialog mới check ≤ Pending.
 */
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import { useState } from 'react'

import { useSnackbar } from '@/hooks/useSnackbar'
import {
  useBulkCreateLotAllocationMutation,
  type FactoryCell,
} from '@/store/api/factoryApi'
import { HIDE_ON_MOBILE_INLINE, TEXT_TRUNCATE } from '@/theme/responsive'

interface MassRow {
  lot_id: string
  stack_id: string
  grn_id: string
  bag_allocated: string
  kg_allocated: string
}

type FieldKey = keyof MassRow

const FIELDS: Array<{ key: FieldKey; label: string; numeric: boolean; required: boolean }> = [
  { key: 'lot_id', label: 'Lot ID', numeric: false, required: true },
  { key: 'stack_id', label: 'Stack ID', numeric: false, required: true },
  { key: 'grn_id', label: 'GRN ID', numeric: false, required: false },
  { key: 'bag_allocated', label: 'Bag Allocated', numeric: true, required: true },
  { key: 'kg_allocated', label: 'Kg Allocated', numeric: true, required: true },
]

const emptyRow = (): MassRow => ({
  lot_id: '',
  stack_id: '',
  grn_id: '',
  bag_allocated: '',
  kg_allocated: '',
})

/** Strip thousand-sep + space → numeric string ('' nếu không parse được). */
function normNumber(raw: string): string {
  const cleaned = raw.replace(/,/g, '').replace(/\s+/g, '').trim()
  if (cleaned === '') return ''
  const n = Number(cleaned)
  return Number.isFinite(n) ? String(n) : ''
}

function coerce(field: FieldKey, raw: string): string {
  const v = (raw ?? '').replace(/\r$/, '')
  return FIELDS.find((f) => f.key === field)?.numeric ? normNumber(v) : v.trim()
}

/** "a\tb\nc\td" → [['a','b'],['c','d']]. */
function parseTsv(text: string): string[][] {
  const trimmed = text.replace(/\r/g, '').replace(/\n$/, '')
  if (!trimmed) return []
  return trimmed.split('\n').map((line) => line.split('\t'))
}

function toNum(v: string): number | null {
  const s = v.replace(/,/g, '').trim()
  if (!s) return null
  const n = Number(s)
  return Number.isFinite(n) ? n : null
}

interface Props {
  open: boolean
  factoryCode: string
  onClose: () => void
  /** Gọi sau khi upload thành công (parent có thể refetch / đóng). */
  onUploaded?: () => void
}

export function LotAllocationMassUploadDialog({ open, factoryCode, onClose, onUploaded }: Props) {
  const snack = useSnackbar()
  const [rows, setRows] = useState<MassRow[]>([emptyRow()])
  const [selected, setSelected] = useState<Set<number>>(new Set())
  const [bulkCreate, { isLoading: uploading }] = useBulkCreateLotAllocationMutation()

  const reset = () => {
    setRows([emptyRow()])
    setSelected(new Set())
  }

  const handleClose = () => {
    if (uploading) return
    onClose()
  }

  const setCell = (rowIdx: number, field: FieldKey, value: string) =>
    setRows((cur) => cur.map((r, i) => (i === rowIdx ? { ...r, [field]: value } : r)))

  // Paste TSV bắt đầu từ ô (rowIdx, colIdx) — mở rộng grid xuống/sang phải.
  const handlePaste = (rowIdx: number, colIdx: number, e: React.ClipboardEvent<HTMLElement>) => {
    const text = e.clipboardData?.getData('text') ?? ''
    if (!text || (!text.includes('\t') && !text.includes('\n'))) return
    e.preventDefault()
    e.stopPropagation()
    const matrix = parseTsv(text)
    setRows((cur) => {
      const next = cur.map((r) => ({ ...r }))
      matrix.forEach((line, r) => {
        const targetRow = rowIdx + r
        while (next.length <= targetRow) next.push(emptyRow())
        line.forEach((cellRaw, c) => {
          const targetCol = colIdx + c
          const fld = FIELDS[targetCol]
          if (!fld) return
          next[targetRow][fld.key] = coerce(fld.key, cellRaw)
        })
      })
      return next
    })
  }

  const toggleRow = (idx: number) =>
    setSelected((cur) => {
      const next = new Set(cur)
      if (next.has(idx)) next.delete(idx)
      else next.add(idx)
      return next
    })

  const toggleAll = () =>
    setSelected((cur) => (cur.size === rows.length ? new Set() : new Set(rows.map((_, i) => i))))

  const deleteSelected = () => {
    if (selected.size === 0) return
    setRows((cur) => {
      const kept = cur.filter((_, i) => !selected.has(i))
      return kept.length ? kept : [emptyRow()]
    })
    setSelected(new Set())
  }

  const runUpload = async () => {
    // Bỏ dòng trống hoàn toàn (paste hay để dư dòng cuối).
    const filled = rows.filter((r) => FIELDS.some((f) => r[f.key].trim() !== ''))
    if (filled.length === 0) {
      snack.warning('At least 1 row is required')
      return
    }
    for (const [index, row] of filled.entries()) {
      const idx = index + 1
      if (!row.lot_id.trim()) {
        snack.warning(`Row ${idx}: missing Lot ID`)
        return
      }
      if (!row.stack_id.trim()) {
        snack.warning(`Row ${idx}: missing Stack ID`)
        return
      }
      const bag = toNum(row.bag_allocated)
      if (bag == null || bag <= 0) {
        snack.warning(`Row ${idx}: Bag Allocated must be > 0`)
        return
      }
      const kg = toNum(row.kg_allocated)
      if (kg == null || kg <= 0) {
        snack.warning(`Row ${idx}: Kg Allocated must be > 0`)
        return
      }
    }
    const payload: Record<string, FactoryCell | undefined>[] = filled.map((row) => ({
      lot_id: row.lot_id.trim(),
      stack_id: row.stack_id.trim(),
      grn_id: row.grn_id.trim() || undefined,
      bag_allocated: toNum(row.bag_allocated),
      kg_allocated: toNum(row.kg_allocated),
    }))
    try {
      const res = await bulkCreate({ factoryCode, rows: payload }).unwrap()
      snack.success(`Uploaded ${res.created}/${filled.length} rows`)
      reset()
      onUploaded?.()
      onClose()
    } catch (err) {
      const detail =
        typeof err === 'object' && err && 'data' in err
          ? (err as { data?: { detail?: string } }).data?.detail
          : undefined
      snack.error(detail || 'Could not upload rows')
    }
  }

  const allSelected = rows.length > 0 && selected.size === rows.length

  return (
    <Dialog
      open={open}
      onClose={(_e, reason) => {
        if (uploading || reason === 'backdropClick') return
        handleClose()
      }}
      maxWidth="lg"
      fullWidth
      scroll="paper"
      PaperProps={{ sx: { maxHeight: 'min(720px, 92vh)' } }}
    >
      <DialogTitle sx={{ pb: 1, pt: 1.25, display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography
          component="span"
          noWrap
          title="Mass upload — Lot Allocation"
          sx={{ ...TEXT_TRUNCATE, flex: 1, fontWeight: 700, fontSize: '1rem', minWidth: 0 }}
        >
          <Box component="span" sx={HIDE_ON_MOBILE_INLINE}>
            {'Mass upload — '}
          </Box>
          Lot Allocation
        </Typography>
        <Typography component="span" variant="caption" color="text.secondary">
          {factoryCode.toUpperCase()}
        </Typography>
      </DialogTitle>
      <Divider />
      <DialogContent dividers sx={{ pt: 1.5 }}>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          Paste from Excel (columns: Lot ID, Stack ID, GRN ID, Bag Allocated, Kg Allocated). GRN ID
          is optional. Empty rows are skipped on upload.
        </Typography>
        <Stack direction="row" spacing={0.5} sx={{ mb: 1 }}>
          <Button size="small" variant="outlined" onClick={() => setRows((c) => [...c, emptyRow()])}>
            Add row
          </Button>
          <Button
            size="small"
            variant="outlined"
            color="error"
            disabled={selected.size === 0}
            onClick={deleteSelected}
          >
            Delete selected
          </Button>
          <Button size="small" variant="outlined" color="inherit" onClick={reset}>
            Reset
          </Button>
        </Stack>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: `40px 40px repeat(${FIELDS.length}, minmax(120px, 1fr))`,
            border: (t) => `1px solid ${t.palette.divider}`,
            borderRadius: 1,
            overflow: 'hidden',
            '& > .hdr': {
              bgcolor: 'action.hover',
              fontWeight: 700,
              fontSize: '0.72rem',
              px: 1,
              py: 0.75,
              borderBottom: (t) => `1px solid ${t.palette.divider}`,
            },
          }}
        >
          {/* Header row */}
          <Box className="hdr" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Checkbox size="small" checked={allSelected} onChange={toggleAll} sx={{ p: 0 }} />
          </Box>
          <Box className="hdr" sx={{ textAlign: 'center' }}>#</Box>
          {FIELDS.map((f) => (
            <Box key={f.key} className="hdr">
              {f.label}
              {f.required ? <span style={{ color: '#d32f2f' }}> *</span> : null}
            </Box>
          ))}

          {/* Data rows */}
          {rows.map((row, rowIdx) => (
            <RowCells
              key={rowIdx}
              row={row}
              rowIdx={rowIdx}
              selected={selected.has(rowIdx)}
              onToggle={() => toggleRow(rowIdx)}
              onCell={(field, value) => setCell(rowIdx, field, value)}
              onPaste={handlePaste}
            />
          ))}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 2, py: 1 }}>
        <Typography variant="caption" color="text.secondary" sx={{ mr: 'auto' }}>
          {rows.length} row{rows.length === 1 ? '' : 's'}
        </Typography>
        <Button size="small" variant="outlined" onClick={handleClose} disabled={uploading}>
          Cancel
        </Button>
        <Button
          size="small"
          variant="contained"
          color="success"
          onClick={() => void runUpload()}
          disabled={uploading}
        >
          {uploading ? <CircularProgress size={16} color="inherit" /> : 'Upload'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

function RowCells({
  row,
  rowIdx,
  selected,
  onToggle,
  onCell,
  onPaste,
}: {
  row: MassRow
  rowIdx: number
  selected: boolean
  onToggle: () => void
  onCell: (field: FieldKey, value: string) => void
  onPaste: (rowIdx: number, colIdx: number, e: React.ClipboardEvent<HTMLElement>) => void
}) {
  const cellSx = {
    borderBottom: (t: { palette: { divider: string } }) => `1px solid ${t.palette.divider}`,
    '& .MuiInputBase-root': { borderRadius: 0, fontSize: '0.78rem' },
    '& .MuiInputBase-input': { py: 0.5, px: 1 },
    '& fieldset': { border: 'none' },
  }
  return (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderBottom: (t) => `1px solid ${t.palette.divider}`,
          bgcolor: selected ? 'action.selected' : undefined,
        }}
      >
        <Checkbox size="small" checked={selected} onChange={onToggle} sx={{ p: 0 }} />
      </Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '0.72rem',
          color: 'text.secondary',
          borderBottom: (t) => `1px solid ${t.palette.divider}`,
        }}
      >
        {rowIdx + 1}
      </Box>
      {FIELDS.map((f, colIdx) => (
        <Tooltip key={f.key} title="" disableHoverListener>
          <TextField
            value={row[f.key]}
            onChange={(e) => onCell(f.key, e.target.value)}
            onPasteCapture={(e) => onPaste(rowIdx, colIdx, e)}
            size="small"
            fullWidth
            variant="outlined"
            inputProps={f.numeric ? { style: { textAlign: 'right' }, inputMode: 'decimal' } : undefined}
            sx={cellSx}
          />
        </Tooltip>
      ))}
    </>
  )
}

export default LotAllocationMassUploadDialog
